import React, { ReactNode, useCallback, useMemo } from 'react'
import { Placement } from 'react-joyride'
import { useMount, useSetState } from 'react-use'

import { PROGRAM_FILTERS } from 'app/constants/home-links.constants'
import useFilteredAppLinks from 'app/hooks/use-filtered-app-links.hook'
import useIsLogoutButtonVisible from 'app/hooks/use-is-logout-button-visible'
import useIsProgramSelectorVisible from 'app/hooks/use-is-program-selector-visible'
import { useAppSelector } from 'app/store'

import slidesData from './tutorial-settings-constants'
import {
  TutorialSettings,
  TutorialSettingsContext,
} from './tutorial-settings.context'

const {
  ALL_APPS,
  CKLA,
  CKLA3,
  CAMINOS,
  CAMINOS_3,
  CAMINOS_3_CA,
  TEXAS_ELAR,
  TEXAS_SLAR,
  TEXAS_ELAR_2E,
  TEXAS_SLAR_2E,
  TEXAS_IMRA_ELAR,
  TEXAS_IMRA_SLAR,
  TEXAS_IMRA_COMMERCIAL,
} = PROGRAM_FILTERS

type SlideSettings = {
  text: string
  target: string
  stepIndex: number
  placement?: Placement | 'auto' | 'center'
}

type TutorialSettingsProviderProps = {
  children: ReactNode
}

const TutorialSettingsProvider = ({
  children,
}: TutorialSettingsProviderProps) => {
  const [tutorialSettings, setTutorialSettings] = useSetState<TutorialSettings>(
    {
      run: false,
      stepIndex: 0,
      steps: {
        [ALL_APPS]: {
          steps: [],
        },
        [CKLA]: {
          steps: [],
        },
        [CKLA3]: {
          steps: [],
        },
        [CAMINOS]: {
          steps: [],
        },
        [CAMINOS_3]: {
          steps: [],
        },
        [CAMINOS_3_CA]: {
          steps: [],
        },
        [TEXAS_ELAR]: {
          steps: [],
        },
        [TEXAS_SLAR]: {
          steps: [],
        },
        [TEXAS_ELAR_2E]: {
          steps: [],
        },
        [TEXAS_SLAR_2E]: {
          steps: [],
        },
        [TEXAS_IMRA_ELAR]: {
          steps: [],
        },
        [TEXAS_IMRA_SLAR]: {
          steps: [],
        },
        [TEXAS_IMRA_COMMERCIAL]: {
          steps: [],
        },
      },
    }
  )
  const isProgramSelectorVisible = useIsProgramSelectorVisible()
  const { isTeacher } = useAppSelector((state) => state.userData.kcInfo)
  const { isLogoutButtonVisible } = useIsLogoutButtonVisible()
  const { grade } = useAppSelector((state) => state.userData)
  const appLinks = useFilteredAppLinks()

  const getSlideSetting = ({
    text,
    target,
    stepIndex,
    placement,
  }: SlideSettings) => ({
    content: <p style={{ margin: 0 }}>{text}</p>,
    placement,
    target,
    stepIndex,
    disableBeacon: true,
    disableOverlayClose: true,
    spotlightPadding: 0,
    floaterProps: {
      disableAnimation: true,
    },
  })

  const getIntroSlide = useCallback(
    () => [
      getSlideSetting({
        text: slidesData.intro.text,
        target: slidesData.intro.targetID,
        stepIndex: tutorialSettings.stepIndex,
        placement: 'center',
      }),
    ],
    [tutorialSettings.stepIndex]
  )

  const getSlidePlacement = useCallback((length: number, index: number) => {
    const shouldThreeInRowLayoutSlideBeAtTheBottom = length > 4 && index < 3
    const shouldTwoInRowLayoutSlideBeAtTheBottom = length === 4 && index < 2

    if (
      shouldTwoInRowLayoutSlideBeAtTheBottom ||
      shouldThreeInRowLayoutSlideBeAtTheBottom
    ) {
      return 'bottom'
    }
    return 'top'
  }, [])

  const getSlidesForProgram = useCallback(
    (program: string) => {
      if (!grade || grade.length === 0) {
        return []
      }
      const programApps = appLinks[grade][program]

      return programApps.map((displayedApp, index) =>
        getSlideSetting({
          text: slidesData[displayedApp.id].text,
          stepIndex: index,
          placement: getSlidePlacement(programApps.length, index),
          target: slidesData[displayedApp.id].targetID,
        })
      )
    },
    [appLinks, grade, getSlidePlacement]
  )

  const getSlideForProgramSelector = useCallback(() => {
    if (isProgramSelectorVisible) {
      return [
        getSlideSetting({
          text: slidesData.programSelector.text,
          target: slidesData.programSelector.targetID,
          stepIndex: tutorialSettings.stepIndex,
          placement: 'bottom',
        }),
      ]
    }
    return []
  }, [isProgramSelectorVisible, tutorialSettings.stepIndex])

  const getSlidesForTeacherButtons = useCallback(() => {
    if (isTeacher) {
      return [
        getSlideSetting({
          text: slidesData.gradeSelector.text,
          stepIndex: tutorialSettings.stepIndex,
          target: slidesData.gradeSelector.targetID,
          placement: 'left',
        }),
        getSlideSetting({
          text: slidesData.teacherGuide.text,
          placement: 'bottom',
          target: slidesData.teacherGuide.targetID,
          stepIndex: tutorialSettings.stepIndex,
        }),
        getSlideSetting({
          text: slidesData.replayTutorial.text,
          placement: 'right',
          target: slidesData.replayTutorial.targetID,
          stepIndex: tutorialSettings.stepIndex,
        }),
      ]
    }
    return []
  }, [isTeacher, tutorialSettings.stepIndex])

  const getSlideForLogoutButton = useCallback(() => {
    if (isLogoutButtonVisible) {
      return [
        getSlideSetting({
          text: slidesData.logoutButton.text,
          placement: 'right',
          target: slidesData.logoutButton.targetID,
          stepIndex: tutorialSettings.stepIndex,
        }),
      ]
    }
    return []
  }, [isLogoutButtonVisible, tutorialSettings.stepIndex])

  const getSlides = useCallback(
    (program: string) => [
      ...getIntroSlide(),
      ...getSlidesForProgram(program),
      ...getSlideForProgramSelector(),
      ...getSlidesForTeacherButtons(),
      ...getSlideForLogoutButton(),
    ],
    [
      getIntroSlide,
      getSlidesForProgram,
      getSlideForProgramSelector,
      getSlidesForTeacherButtons,
      getSlideForLogoutButton,
    ]
  )

  const getSteps = useCallback(
    () => ({
      [ALL_APPS]: {
        steps: [...getSlides(ALL_APPS)],
      },
      [CKLA]: {
        steps: [...getSlides(CKLA)],
      },
      [CKLA3]: {
        steps: [...getSlides(CKLA3)],
      },
      [CAMINOS]: {
        steps: [...getSlides(CAMINOS)],
      },
      [CAMINOS_3]: {
        steps: [...getSlides(CAMINOS_3)],
      },
      [CAMINOS_3_CA]: {
        steps: [...getSlides(CAMINOS_3_CA)],
      },
      [TEXAS_ELAR]: {
        steps: [...getSlides(TEXAS_ELAR)],
      },
      [TEXAS_SLAR]: {
        steps: [...getSlides(TEXAS_SLAR)],
      },
      [TEXAS_IMRA_ELAR]: {
        steps: [...getSlides(TEXAS_IMRA_ELAR)],
      },
      [TEXAS_IMRA_SLAR]: {
        steps: [...getSlides(TEXAS_IMRA_SLAR)],
      },
      [TEXAS_IMRA_COMMERCIAL]: {
        steps: [...getSlides(TEXAS_IMRA_COMMERCIAL)],
      },
      [TEXAS_ELAR_2E]: {
        steps: [...getSlides(TEXAS_ELAR_2E)],
      },
      [TEXAS_SLAR_2E]: {
        steps: [...getSlides(TEXAS_SLAR_2E)],
      },
    }),
    [getSlides]
  )

  useMount(() => {
    setTutorialSettings({
      steps: getSteps(),
    })
  })

  const handlePlay = useCallback(() => {
    setTutorialSettings({
      run: true,
      stepIndex: 0,
      steps: getSteps(),
    })
  }, [setTutorialSettings, getSteps])

  const handleStop = useCallback(() => {
    setTutorialSettings({ run: false, stepIndex: 0 })
  }, [setTutorialSettings])

  const contextProviderValue = useMemo(
    () => ({
      tutorialSettings,
      setTutorialSettings,
      handlePlay,
      handleStop,
    }),
    [tutorialSettings, setTutorialSettings, handlePlay, handleStop]
  )

  return (
    <TutorialSettingsContext.Provider value={contextProviderValue}>
      {children}
    </TutorialSettingsContext.Provider>
  )
}

export default TutorialSettingsProvider
